import React from 'react';
import CourseTemplate from '../../components/CourseTemplate';

const NuclearMedicineTechnologyCoursePage: React.FC = () => {
  const nuclearMedData = {
    title: "Bachelor of Science in Nuclear Medicine Technology",
    shortName: "B.Sc NMT",
    duration: "3 Years",
    description: "A specialized program focusing on nuclear imaging, radiopharmaceuticals, and nuclear medicine procedures for diagnosis and treatment of diseases.",
    overview: "B.Sc Nuclear Medicine Technology prepares students to become nuclear medicine technologists who use radioactive materials for diagnostic imaging and therapeutic procedures. The program covers nuclear physics, radiopharmacy, imaging techniques, radiation safety, and patient care. Students learn to operate sophisticated nuclear medicine equipment including gamma cameras, PET scanners, and SPECT systems for diagnosing and treating various medical conditions.",
    eligibility: [
      "Completed 10+2 with Physics, Chemistry, and Biology/Mathematics",
      "Minimum 50% aggregate marks (45% for SC/ST/OBC)",
      "Age: 17-25 years",
      "Good understanding of physics and mathematics",
      "Interest in advanced medical technology",
      "Attention to detail and safety protocols"
    ],
    careerProspects: {
      roles: [
        "Nuclear Medicine Technologist",
        "PET/CT Technologist",
        "SPECT Technologist",
        "Radiopharmacy Technician",
        "Radiation Safety Officer",
        "Nuclear Imaging Specialist",
        "Research Technician",
        "Quality Control Specialist",
        "Clinical Applications Specialist",
        "Nuclear Medicine Coordinator"
      ],
      industries: [
        "Hospitals with Nuclear Medicine",
        "Cancer Treatment Centers",
        "Diagnostic Imaging Centers",
        "Nuclear Medicine Facilities",
        "Research Institutions",
        "Pharmaceutical Companies",
        "Medical Equipment Companies",
        "Government Health Services",
        "Academic Medical Centers",
        "Regulatory Bodies"
      ],
      averageSalary: "₹4-10 LPA (varies by experience and specialization)",
      topRecruiters: [
        "Tata Memorial Hospital",
        "AIIMS Nuclear Medicine",
        "Apollo Hospitals",
        "Fortis Healthcare",
        "Medanta Cancer Institute",
        "Manipal Hospitals",
        "GE Healthcare",
        "Siemens Healthineers",
        "Philips Healthcare",
        "Nuclear Power Corporation"
      ]
    },
    curriculum: [
      {
        year: 1,
        subjects: [
          "Nuclear Physics",
          "Atomic Structure",
          "Radiation Physics",
          "Human Anatomy",
          "Basic Physiology",
          "Mathematics",
          "Chemistry",
          "Medical Terminology"
        ]
      },
      {
        year: 2,
        subjects: [
          "Nuclear Medicine Instrumentation",
          "Radiopharmacy",
          "Imaging Techniques",
          "Radiation Detection",
          "Radiation Safety",
          "Pathophysiology",
          "Quality Control",
          "Patient Care"
        ]
      },
      {
        year: 3,
        subjects: [
          "Advanced Imaging (PET/SPECT)",
          "Therapeutic Nuclear Medicine",
          "Clinical Nuclear Medicine",
          "Radiation Protection",
          "Research Methods",
          "Professional Practice",
          "Clinical Internship",
          "Regulatory Affairs"
        ]
      }
    ],
    admissionProcess: [
      "Apply to colleges offering B.Sc NMT",
      "Entrance examination (typically NEET-based)",
      "Merit-based selection",
      "Document verification",
      "Medical fitness certificate",
      "Background verification (for radiation work)",
      "Fee payment and admission",
      "Radiation safety training orientation"
    ],
    topColleges: [
      {
        name: "Tata Memorial Centre",
        location: "Mumbai, Maharashtra",
        fees: "₹1 LPA",
        ranking: "Premier Cancer Institute"
      },
      {
        name: "All India Institute of Medical Sciences",
        location: "New Delhi",
        fees: "₹10,000 PA",
        ranking: "Top Medical Institute"
      },
      {
        name: "Christian Medical College",
        location: "Vellore, Tamil Nadu",
        fees: "₹3.5 LPA",
        ranking: "Premier Medical College"
      },
      {
        name: "Kasturba Medical College",
        location: "Manipal, Karnataka",
        fees: "₹4.5 LPA",
        ranking: "Deemed University"
      },
      {
        name: "Government Medical College",
        location: "Mumbai, Maharashtra",
        fees: "₹50,000 PA",
        ranking: "Government Medical College"
      }
    ],
    skills: [
      "Nuclear Instrumentation",
      "Radiation Safety",
      "Technical Proficiency",
      "Quality Control",
      "Patient Care",
      "Attention to Detail",
      "Problem Solving",
      "Documentation Skills",
      "Safety Protocols",
      "Computer Skills",
      "Team Collaboration",
      "Continuous Learning"
    ],
    futureScope: "Nuclear medicine is advancing rapidly with new radiopharmaceuticals, hybrid imaging (PET/MRI), theranostics (therapy + diagnostics), and personalized nuclear medicine. Future opportunities include specialization in molecular imaging, radiopharmaceutical development, artificial intelligence in nuclear medicine, and emerging applications in neurology, cardiology, and oncology.",
    faqs: [
      {
        question: "Is nuclear medicine technology safe to work with?",
        answer: "Yes, when proper radiation safety protocols are followed. Nuclear medicine technologists receive extensive training in radiation protection and safety measures. Exposure levels are carefully monitored and kept within safe limits."
      },
      {
        question: "What is the difference between nuclear medicine and radiology?",
        answer: "Nuclear medicine uses radioactive materials internally to image organ function and metabolism, while radiology uses external radiation (X-rays, CT) to image anatomy and structure."
      },
      {
        question: "Are there opportunities for research in this field?",
        answer: "Yes, significant research opportunities exist in radiopharmaceutical development, new imaging techniques, artificial intelligence applications, and clinical research in various diseases."
      },
      {
        question: "What are theranostics?",
        answer: "Theranostics combines therapy and diagnostics using the same or similar radioactive compounds - first for imaging to identify disease, then for targeted therapy. It's a rapidly growing field in personalized medicine."
      },
      {
        question: "Can I specialize in specific areas?",
        answer: "Yes, specializations include cardiac nuclear medicine, neuro-nuclear medicine, oncologic imaging, pediatric nuclear medicine, and emerging areas like theranostics and molecular imaging."
      }
    ]
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-black flex flex-col items-center justify-start py-8 px-2">
      <h1 className="text-4xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-pink-400 to-purple-400 drop-shadow-glow mb-6 text-center animate-glow">
        {nuclearMedData.title} ({nuclearMedData.shortName})
      </h1>
      <div className="w-full max-w-4xl">
        <CourseTemplate courseData={nuclearMedData} />
      </div>
    </div>
  );
};

export default NuclearMedicineTechnologyCoursePage;